import { Box, FormControl, Grid, Typography } from "@mui/material";
import { memo } from "react";
import { useTranslation } from "react-i18next";
import CustomAsyncAutocomplete from "../../../../../core-components/theme/components/CustomAsyncAutoComplete";
import type { User } from "../../../types";

type AttendanceField = "attendanceManager" | "rosterManager" | "operator";

type PersonOption = Partial<Pick<User, "name" | "email">> & { readonly role?: string };

interface AttendanceSectionProps {
  readonly options: User[];
  readonly attendanceManager: User[];
  readonly rosterManager: User[];
  readonly operator: User[];
  readonly errors: Partial<Record<AttendanceField, string>>;
  readonly onChange: (field: AttendanceField, value: User[]) => void;
}

interface UserFieldProps {
  readonly field: AttendanceField;
  readonly label: string;
  readonly value: User[];
  readonly options: User[];
  readonly error?: string;
  readonly onChange: (field: AttendanceField, value: User[]) => void;
}

const isUser = (picked: unknown): picked is User =>
  typeof picked === "object" && picked !== null && typeof (picked as { _id?: unknown })._id === "string";

const renderPersonOption = (option: PersonOption) => (
  <Box>
    <Typography variant="body" fontWeight="fontWeightMedium">
      {option.name}
    </Typography>
    <Typography variant="info" fontWeight="fontWeightRegular" color="Neutrals.600">
      {option.role} <Box component="span" aria-hidden="true">•</Box> {option.email}
    </Typography>
  </Box>
);

const UserField = ({ field, label, value, options, error, onChange }: UserFieldProps) => {
  const { t } = useTranslation();
  return (
    <FormControl fullWidth error={Boolean(error)}>
      <CustomAsyncAutocomplete
        label={label}
        placeholder={t("escalation.selectPeoplePlaceholder", "Select {{role}}...", { role: label.toLowerCase() })}
        value={value}
        defaultOptions={options}
        labelKey="name"
        mainKey="_id"
        multiple
        onSelect={(picked) => onChange(field, Array.isArray(picked) ? picked.filter(isUser) : [])}
        error={Boolean(error)}
        errorText={error}
        renderCustomOption={renderPersonOption}
      />
    </FormControl>
  );
};

const AttendanceSection = memo(
  ({ attendanceManager, rosterManager, operator, options, errors, onChange }: AttendanceSectionProps) => {
    const { t } = useTranslation();

    return (
      <Grid container spacing={3}>
        <Grid item xs={12}>
          <UserField
            field="attendanceManager"
            label={t("escalation.attendanceManager", "Attendance Manager")}
            value={attendanceManager}
            options={options}
            error={errors.attendanceManager}
            onChange={onChange}
          />
        </Grid>
        <Grid item xs={12}>
          <UserField
            field="rosterManager"
            label={t("escalation.rosterManager", "Roster Manager")}
            value={rosterManager}
            options={options}
            error={errors.rosterManager}
            onChange={onChange}
          />
        </Grid>
        <Grid item xs={12}>
          <UserField
            field="operator"
            label={t("escalation.operator", "Operator")}
            value={operator}
            options={options}
            error={errors.operator}
            onChange={onChange}
          />
        </Grid>
      </Grid>
    );
  },
);

export default AttendanceSection;
